// ClientView — vista por cliente con línea de tiempo del ciclo,
// semáforo de las 13 secciones de la Carpeta AFAC y desglose de
// documentos por fase. Tanda 6.

import { html } from 'htm/preact'
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { route } from 'preact-router'

import {
  initInventory,
  getInventoryState,
  subscribeInventoryChange
} from '../lib/inventory-store.js'
import {
  initValidation,
  getDocumentEntry,
  subscribeValidation
} from '../lib/validation-store.js'
import {
  detectarClientes,
  getDocsForClient,
  aggregateClient,
  computeAllSections,
  detectarFaseActiva,
  estadoFase
} from '../lib/client-aggregator.js'
import { FASES } from '../lib/afac-sections.js'

import { exportClientReportToXlsx } from '../lib/exporter.js'
import { showToast } from '../lib/toast-bus.js'
import { getClienteState } from '../lib/validation-store.js'

import { StatusBadge } from './StatusBadge.js'
import { DocumentDetail } from './DocumentDetail.js'
import { HitosManager } from './HitosManager.js'

const ESTADO_FASE_LABEL = {
  no_iniciada: 'No iniciada',
  en_curso: 'En curso',
  cerrada: 'Cerrada'
}

function Loading({ inv }) {
  return html`
    <div class="client-view">
      <section class="section">
        <h1 class="section__title">Por cliente</h1>
        <p class="section__subtitle">Sincronizando inventario…</p>
        <div class="notice">
          <strong>Cargando.</strong> Documentos escaneados:
          ${' '}<strong>${inv.progress.scanned}</strong>.
        </div>
      </section>
    </div>
  `
}

function EmptyClient({ clientesActivos, current }) {
  return html`
    <div class="client-view">
      <section class="section">
        <h1 class="section__title">Por cliente</h1>
        <p class="section__subtitle">
          ${clientesActivos.length === 0
            ? 'No hay clientes activos en el inventario.'
            : `No se encontraron documentos para el cliente "${current}". Clientes disponibles: ${clientesActivos.join(', ')}.`}
        </p>
      </section>
    </div>
  `
}

export function ClientView({ clienteId }) {
  const [inv, setInv] = useState(getInventoryState())
  const [selectedFile, setSelectedFile] = useState(null)
  const [, forceRender] = useState(0)

  useEffect(() => {
    let cancelled = false
    const unsubInv = subscribeInventoryChange((next) => {
      if (!cancelled) setInv(next)
    })
    const unsubVal = subscribeValidation(() => {
      if (!cancelled) forceRender((v) => v + 1)
    })
    initInventory().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[ClientView] initInventory falló:', err)
    })
    initValidation().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[ClientView] initValidation falló:', err)
    })
    return () => {
      cancelled = true
      unsubInv()
      unsubVal()
    }
  }, [])

  const allFiles = inv.data?.files || []
  const clientesActivos = useMemo(() => detectarClientes(allFiles), [allFiles])
  const decoded = clienteId ? decodeURIComponent(clienteId) : null
  const clienteSeleccionado = decoded || clientesActivos[0] || null

  const handleSelectFile = useCallback((file) => setSelectedFile(file), [])
  const handleCloseDetail = useCallback(() => setSelectedFile(null), [])

  if ((inv.state === 'loading' || inv.state === 'idle') && !inv.data) {
    return html`<${Loading} inv=${inv} />`
  }

  if (!clienteSeleccionado) {
    return html`<${EmptyClient} clientesActivos=${clientesActivos} current=${clienteId} />`
  }

  const clientFiles = getDocsForClient(allFiles, clienteSeleccionado)
  if (clientFiles.length === 0) {
    return html`<${EmptyClient} clientesActivos=${clientesActivos} current=${clienteSeleccionado} />`
  }

  const agg = aggregateClient(clientFiles)
  const secciones = computeAllSections(agg.porSeccion)
  const faseActiva = detectarFaseActiva(agg.porFase)

  const conEstado = clientFiles.filter((f) => getDocumentEntry(f)?.estado).length
  const aprobados = clientFiles.filter(
    (f) => getDocumentEntry(f)?.estado === 'Aprobado'
  ).length
  const porcentajeAprobado =
    conEstado > 0 ? Math.round((aprobados / conEstado) * 100) : null

  const seccionesVerdes = secciones.filter((s) => s.estado === 'verde').length

  const handleExportReport = () => {
    try {
      const enrichedFiles = clientFiles.map((f) => ({
        ...f,
        entry: getDocumentEntry(f)
      }))
      const enrichedPorFase = {}
      for (const [k, lista] of Object.entries(agg.porFase)) {
        enrichedPorFase[k] = lista.map((f) => ({
          ...f,
          entry: getDocumentEntry(f)
        }))
      }
      const hitos = getClienteState(clienteSeleccionado).hitos || []
      const filename = exportClientReportToXlsx(clienteSeleccionado, {
        clientFiles: enrichedFiles,
        secciones,
        porFase: enrichedPorFase,
        hitos
      })
      showToast({
        message: `Reporte exportado: ${filename}`,
        kind: 'success'
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ClientView] export falló:', err)
      showToast({
        message: 'No se pudo generar el reporte. Ver consola.',
        kind: 'error'
      })
    }
  }

  return html`
    <div class="client-view">
      <section class="section">
        <div class="client-header">
          <div>
            <div class="client-header__eyebrow">Cliente</div>
            <h1 class="section__title">${clienteSeleccionado}</h1>
            <p class="section__subtitle">
              Fase activa: <strong>${faseActiva}</strong> ·
              ${clientFiles.length} documento${clientFiles.length === 1 ? '' : 's'} asociado${clientFiles.length === 1 ? '' : 's'}
            </p>
          </div>
          <div class="client-header__actions">
            ${clientesActivos.length > 1
              ? html`
                  <label class="client-header__select-wrapper">
                    <span class="inventory__filter-label">Cambiar cliente</span>
                    <select
                      class="inventory__select"
                      value=${clienteSeleccionado}
                      onChange=${(e) => route(`/clientes/${encodeURIComponent(e.target.value)}`)}
                    >
                      ${clientesActivos.map(
                        (c) => html`<option key=${c} value=${c}>${c}</option>`
                      )}
                    </select>
                  </label>
                `
              : null}
            <button
              type="button"
              class="btn btn--primary"
              onClick=${handleExportReport}
              title="Generar reporte ejecutivo del cliente en Excel"
            >Exportar reporte cliente</button>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="kpi-grid">
          <article class="kpi-card">
            <div class="kpi-card__label">Documentos del cliente</div>
            <div class="kpi-card__value">${clientFiles.length}</div>
            <div class="kpi-card__hint">Asociados a 03_Clientes/Cliente_${clienteSeleccionado}/</div>
          </article>
          <article class="kpi-card">
            <div class="kpi-card__label">Con estado registrado</div>
            <div class="kpi-card__value">${conEstado}</div>
            <div class="kpi-card__hint">${clientFiles.length > 0 ? `${Math.round((conEstado / clientFiles.length) * 100)}% del total` : '—'}</div>
          </article>
          <article class="kpi-card">
            <div class="kpi-card__label">% Aprobados</div>
            <div class="kpi-card__value">${porcentajeAprobado == null ? '—' : `${porcentajeAprobado}%`}</div>
            <div class="kpi-card__hint">Sobre los documentos con estado</div>
          </article>
          <article class="kpi-card">
            <div class="kpi-card__label">Carpeta AFAC</div>
            <div class="kpi-card__value">${seccionesVerdes}/13</div>
            <div class="kpi-card__hint">Secciones completas</div>
          </article>
        </div>
      </section>

      <section class="section">
        <h2>Ciclo del proyecto</h2>
        <p class="section__subtitle">Línea de tiempo de las 7 fases. Verde = cerrada; azul = en curso; gris = no iniciada.</p>
        <ol class="timeline">
          ${FASES.map((fase) => {
            const docs = agg.porFase[`Fase ${fase.numero}`]
            const estado = estadoFase(docs)
            return html`
              <li
                key=${fase.numero}
                class=${`timeline__step timeline__step--${estado}`}
                title=${`${ESTADO_FASE_LABEL[estado]} · ${docs.length} doc${docs.length === 1 ? '' : 's'}`}
              >
                <div class="timeline__dot">
                  ${estado === 'cerrada' ? '✓' : fase.numero}
                </div>
                <div class="timeline__label">Fase ${fase.numero}</div>
                <div class="timeline__name">${fase.nombre}</div>
                <div class="timeline__meta">${docs.length} doc${docs.length === 1 ? '' : 's'}</div>
              </li>
            `
          })}
        </ol>
      </section>

      <section class="section">
        <h2>Carpeta AFAC — Estado de las 13 secciones</h2>
        <p class="section__subtitle">
          Verde: completa. Ámbar: parcial. Rojo: sin documentos o solo placeholders.
          Las secciones marcadas "No JMA" corresponden a entregables de UVA o autoridad.
        </p>
        <div class="afac-sections">
          ${secciones.map(
            (sec) => html`
              <article
                key=${sec.numero}
                class=${`afac-section afac-section--${sec.estado}`}
                title=${`${sec.mensaje} · ${sec.docs.length} archivo(s)`}
              >
                <div class="afac-section__head">
                  <span class="afac-section__num">${String(sec.numero).padStart(2, '0')}</span>
                  ${sec.noJMA
                    ? html`<span
                        class="afac-section__no-jma"
                        title="Documento de terceros (UVA o autoridad). JMA no lo produce."
                      >Responsabilidad externa</span>`
                    : null}
                </div>
                <div class="afac-section__name">${sec.nombre}</div>
                <div class="afac-section__status">${sec.mensaje}</div>
              </article>
            `
          )}
        </div>
      </section>

      <section class="section">
        <${HitosManager} clienteId=${clienteSeleccionado} />
      </section>

      <section class="section">
        <h2>Documentos por fase</h2>
        <p class="section__subtitle">Clic en un documento para abrir su detalle.</p>
        <div class="phase-blocks">
          ${FASES.map((fase) => {
            const docs = agg.porFase[`Fase ${fase.numero}`]
            if (docs.length === 0) return null
            const aprobadosFase = docs.filter(
              (d) => getDocumentEntry(d)?.estado === 'Aprobado'
            ).length
            return html`
              <div class="phase-block" key=${fase.numero}>
                <h3 class="phase-block__title">
                  Fase ${fase.numero} — ${fase.nombre}
                  <span class="phase-block__count">${aprobadosFase}/${docs.length}</span>
                </h3>
                <ul class="phase-block__docs">
                  ${docs.map((file) => {
                    const entry = getDocumentEntry(file)
                    return html`
                      <li class="phase-block__doc" key=${file.id || file.relativePath}>
                        <button
                          type="button"
                          class="phase-block__doc-btn"
                          onClick=${() => handleSelectFile(file)}
                          title=${file.relativePath}
                        >
                          <span class="phase-block__doc-name">${file.name}</span>
                          <span class="phase-block__doc-meta">
                            ${file.metadata?.codigoEntregable || '—'}
                            ${file.metadata?.version ? html` · v${file.metadata.version}` : null}
                          </span>
                          <${StatusBadge} estado=${entry?.estado} size="sm" />
                        </button>
                      </li>
                    `
                  })}
                </ul>
              </div>
            `
          })}
        </div>
      </section>

      ${selectedFile
        ? html`<${DocumentDetail} file=${selectedFile} onClose=${handleCloseDetail} />`
        : null}
    </div>
  `
}
